/**
 * Caixa de seleção do design system.
 *
 * O `<input type="checkbox">` nativo continua no DOM (escondido visualmente,
 * nunca removido): é ele que recebe teclado, foco, formulário e leitor de
 * tela. A caixa violeta é só a pele, desenhada pelo estado do input (`peer`).
 * O rótulo fica do lado (nunca placeholder fazendo as vezes de label,
 * anti-padrão 12) e a linha inteira tem 44px de alvo no celular.
 */
import { forwardRef, useEffect, useId, useImperativeHandle, useRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '../lib/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  /** Linha de ajuda abaixo do rótulo (ligada por aria-describedby). */
  hint?: ReactNode;
  /** Alias de `hint`, com o nome usado no Claude Design. */
  description?: ReactNode;
  /** "Alguns selecionados" — ex.: o cabeçalho de uma tabela com seleção parcial. */
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, description, indeterminate = false, className, id, disabled, ...props },
  ref,
) {
  const generated = useId();
  const inputId = id ?? `checkbox-${generated}`;
  const help = hint ?? description;
  const hintId = `${inputId}-hint`;

  const inner = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inner.current as HTMLInputElement);
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label
        htmlFor={inputId}
        className={cn(
          // `relative` ancora o input `sr-only` (absoluto) no próprio label. Sem isso ele se
          // posiciona contra o <dialog>: o modal ganhava 3968px de altura falsa e, no foco do
          // clique, rolava ~1000px — a lista "voltava" a cada grupo marcado (Shermam, 05/10).
          'relative inline-flex items-center gap-2.5 text-sm text-foreground select-none max-md:min-h-11',
          disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer',
        )}
      >
        <input
          ref={inner}
          id={inputId}
          type="checkbox"
          disabled={disabled}
          aria-describedby={help ? hintId : undefined}
          aria-checked={indeterminate ? 'mixed' : undefined}
          className="peer sr-only"
          {...props}
        />
        <span
          aria-hidden="true"
          // borda de 1,5px: a escala do Tailwind só tem 1 e 2, e a caixa de 18px pede o meio
          className={cn(
            // oxlint-disable-next-line eslint-js/no-restricted-syntax -- borda de 1,5px da caixa é especificação do export; não há token de traço
            'grid size-4.5 shrink-0 place-items-center rounded-sm border-[1.5px] border-border-strong bg-surface text-primary-foreground',
            'transition-colors duration-[var(--duration-micro)]',
            '[&_svg]:size-3 [&_svg]:opacity-0',
            'peer-checked:border-primary peer-checked:bg-primary peer-checked:[&_svg.check]:opacity-100',
            indeterminate && 'border-primary bg-primary [&_svg.minus]:opacity-100',
            'peer-focus-visible:ring-3 peer-focus-visible:ring-ring/40',
          )}
        >
          {indeterminate ? <Minus className="minus" strokeWidth={3} /> : <Check className="check" strokeWidth={3} />}
        </span>
        {label ? <span className="min-w-0">{label}</span> : null}
      </label>
      {help ? (
        <p id={hintId} className="pl-7 text-xs text-subtle-foreground">
          {help}
        </p>
      ) : null}
    </div>
  );
});
